import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config';

const AUTH_TIMEOUT_MS = 15000;

/**
 * Guard component that requires HTTP Basic Authentication
 * Prompts for username/password if not authenticated
 */
export default function RequireAuth({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const verifyCredentials = async (user, pass) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), AUTH_TIMEOUT_MS);

    try {
      const response = await fetch(apiUrl('/admin/responses'), {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Authorization': `Basic ${btoa(`${user}:${pass}`)}`,
        },
      });

      if (response.ok) {
        setIsAuthenticated(true);
        setError('');
        // Store credentials in localStorage
        localStorage.setItem('adminAuth', JSON.stringify({ username: user, password: pass }));
      } else {
        setIsAuthenticated(false);
        setError('Invalid credentials');
        localStorage.removeItem('adminAuth');
      }
    } catch (err) {
      setIsAuthenticated(false);
      setError(
        err.name === 'AbortError'
          ? 'Authentication timed out. Please try again.'
          : 'Authentication failed'
      );
      localStorage.removeItem('adminAuth');
    } finally {
      window.clearTimeout(timeoutId);
      setIsChecking(false);
    }
  };

  // Check if credentials are stored in localStorage
  useEffect(() => {
    const storedAuth = localStorage.getItem('adminAuth');
    if (storedAuth) {
      try {
        const { username: storedUsername, password: storedPassword } = JSON.parse(storedAuth);
        setUsername(storedUsername);
        setPassword(storedPassword);
        verifyCredentials(storedUsername, storedPassword);
      } catch (e) {
        localStorage.removeItem('adminAuth');
        setIsChecking(false);
      }
    } else {
      setIsChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setIsChecking(true);
    verifyCredentials(username, password);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Admin Authentication
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Please enter your credentials to access the admin area
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="username" className="sr-only">
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-sky-500 focus:border-sky-500 focus:z-10 sm:text-sm"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-sky-500 focus:border-sky-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-800">{error}</div>
              </div>
            )}

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
              >
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return children;
}
